"use client";

import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import Image from "next/image";
import { ArrowDown, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/common/Button";
import { CountUpStat } from "@/components/common/CountUpStat";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Layout } from "@/components/layout/Layout";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { projects } from "@/content/projects";

import { ROUTES } from "@/config/routes";

const heroStats = [
  { end: 20, suffix: "+", label: "Years experience" },
  { end: 2000, suffix: "+", label: "Happy families" },
  { end: 500, suffix: "+", label: "Acres developed" },
  { end: 5, suffix: "+", label: "Project communities" },
];

const contourPaths = [
  "M -40 70 C 160 10, 280 180, 160 300 S 120 580, 520 690",
  "M -16 70 C 170 10, 274 180, 164 300 S 136 580, 511 690",
  "M 8 70 C 180 10, 268 180, 168 300 S 152 580, 502 690",
  "M 32 70 C 190 10, 262 180, 172 300 S 168 580, 493 690",
  "M 56 70 C 200 10, 256 180, 176 300 S 184 580, 484 690",
  "M 80 70 C 210 10, 250 180, 180 300 S 200 580, 475 690",
];

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const wordReveal = {
  hidden: { opacity: 0, y: "100%", rotate: 2 },
  visible: { opacity: 1, y: 0, rotate: 0 },
};

export default function Home() {
  return (
    <Layout title="DTCP, RERA and MUDA Open Plots near Shadnagar | Swargaseema" image="/hero/drone-shot-poster.jpg">
      <main>
        <InteractiveHero />

        <section className="py-16 lg:py-24">
          <Container>
            <SectionHeading
              eyebrow="Projects"
              title="Compare communities before you enquire."
              copy="Each project gives buyers the essentials: status, location, approvals, scale, and a direct path to the full details."
            />
            <div className="mt-9 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.slice(0, 3).map((project, index) => (
                <motion.div
                  key={project.slug}
                  initial={{ opacity: 0, y: 36 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </div>
          </Container>
        </section>

        <section className="bg-[var(--page-soft)] py-16 lg:py-24 border-t border-[var(--border-subtle)]">
          <Container className="grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <motion.div
              initial={{ opacity: 0, x: -36 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.75 }}
              className="relative flex h-[430px] items-start justify-center overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-raised)] border border-[var(--border-subtle)] shadow-[var(--shadow-md)] px-6 pt-8 sm:h-[500px] lg:h-[560px]"
            >
              <Image
                src="/brand/founder-about-relaxed.webp"
                alt="Founder of Swargaseema"
                width={664}
                height={1300}
                sizes="(max-width: 800px) 86vw, 38vw"
                className="h-auto max-h-[700px] w-auto max-w-full object-contain object-top lg:max-h-[820px]"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 36 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.75 }}
            >
              <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--accent)]">Founder message</p>
              <h2 className="font-serif text-3xl font-medium leading-tight text-[var(--text-strong)] sm:text-4xl lg:text-5xl">Meet Mr. Chandra Chandrasekhar</h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-[var(--text-muted)]">
                More than two decades of real-estate experience, focused on transparent approvals, considered locations, and long-term value.
              </p>
              <div className="mt-7">
                <Link href={ROUTES.ABOUT}>
                  <Button variant="secondary" type="button">Read about Swargaseema <ArrowRight size={16} /></Button>
                </Link>
              </div>
            </motion.div>
          </Container>
        </section>
      </main>
    </Layout>
  );
}

function HeroVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPlayback = () => {
      if (!videoRef.current) return;
      if (media.matches) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => { });
      }
    };

    syncPlayback();
    media.addEventListener("change", syncPlayback);
    return () => media.removeEventListener("change", syncPlayback);
  }, []);

  return (
    <video
      ref={videoRef}
      className="hero-video absolute inset-0 h-full w-full object-cover"
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster="/hero/drone-shot-poster.jpg"
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src="/hero/drone-shot-video.mp4" type="video/mp4" />
    </video>
  );
}

function InteractiveHero() {
  return (
    <section
      className="hero-video-shell relative isolate flex min-h-[760px] overflow-hidden bg-[#071a14] text-white lg:min-h-[min(940px,100svh)]"
      aria-labelledby="home-hero-title"
    >
      <HeroVideo />

      {/* Targeted gradient just for the left text area */}
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-full md:w-3/4 lg:w-1/2 bg-gradient-to-r from-[rgba(0,0,0,0.8)] via-[rgba(0,0,0,0.4)] to-transparent" />

      {/* Targeted gradient just for the bottom stats area */}
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[rgba(0,0,0,0.8)] to-transparent" />

      {/* Completely removed the hero-grain to eliminate any blurriness */}

      <div className="relative z-10 mx-auto flex w-[calc(100%_-_clamp(40px,7vw,112px))] max-w-[1840px] flex-1 flex-col justify-end pb-8 pt-32 lg:pb-10 lg:pt-40">
        <motion.div initial="hidden" animate="visible" transition={{ staggerChildren: 0.14 }} className="max-w-[780px] pb-12 lg:pb-20">
          <motion.h1
            id="home-hero-title"
            className="max-w-[760px] font-serif text-[2.2rem] sm:text-[clamp(3.3rem,7.1vw,7.4rem)] font-normal leading-[1.02] sm:leading-[0.88] tracking-[-0.035em] text-white"
          >
            <span className="block overflow-hidden pb-2">
              {["Land", "that", "grows"].map((word, index) => (
                <motion.span key={index} className="inline-block mr-[0.25em]" variants={wordReveal} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>{word}</motion.span>
              ))}
            </span>
            <span className="block overflow-hidden pb-2">
              {["with", "your"].map((word, index) => (
                <motion.span key={index} className="inline-block mr-[0.25em]" variants={wordReveal} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>{word}</motion.span>
              ))}
              <motion.span className="inline-block text-white" variants={wordReveal} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>future.</motion.span>
            </span>
          </motion.h1>

          <motion.p variants={reveal} transition={{ duration: 0.7 }} className="mt-7 max-w-[330px] text-base sm:max-w-[610px] leading-7 text-white/78 sm:text-lg sm:leading-8">
            Premium plotted communities across South Hyderabad, selected for clear approvals, strong connectivity, and long-term value.
          </motion.p>

          <motion.div variants={reveal} transition={{ duration: 0.7 }} className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link href={ROUTES.PROJECTS}>
              <Button className="group/button min-h-14 rounded-none px-7" type="button">
                Explore Projects <ArrowRight size={18} className="transition-transform group-hover/button:translate-x-1" />
              </Button>
            </Link>
            <Link href={ROUTES.CONTACT_US}>
              <Button variant="secondary" className="min-h-14 rounded-none border-white/45 bg-transparent px-7 backdrop-blur-sm text-white" type="button">
                <CalendarDays size={18} /> Book a Site Visit
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.75 }}
          className="grid grid-cols-2 border-t border-white/25 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]"
        >
          {heroStats.map((stat) => (
            <div key={stat.label} className="border-b border-white/15 py-4 pr-3 sm:py-5 sm:pr-6 sm:border-r lg:border-b-0 lg:pl-6 lg:first:pl-0">
              <CountUpStat end={stat.end} suffix={stat.suffix} label={stat.label} />
              <span className="mt-1 block text-[10px] font-bold uppercase tracking-[0.14em] text-white/60">{stat.label}</span>
            </div>
          ))}
          <a href="#locations" className="hidden items-center gap-3 pl-8 text-xs font-bold uppercase tracking-[0.16em] text-white/70 transition hover:text-[var(--gold-strong)] lg:flex">
            Discover <ArrowDown size={16} />
          </a>
        </motion.div>
      </div>

      <div aria-hidden="true" className="absolute right-[5vw] top-1/2 hidden -translate-y-1/2 items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 xl:flex">
        <MapPin size={14} className="text-[var(--gold-strong)]" /> South Hyderabad
      </div>
    </section>
  );
}
